/**
 * Phase 119 — On-Login PWA Installation Banner
 *
 * Renders a high-converting, dismissible installation card at the top of
 * the authenticated dashboard.  Three states:
 *
 *   1. canInstall = true   → big logo click target + "click to install"
 *   2. isIos      = true   → Safari Share-icon instructions (BIP unsupported)
 *   3. installed / standalone / dismissed → null
 *
 * Dismissal persists via `localStorage['outplay_prompt_dismissed']` per
 * the Phase 119 spec.  Cleared automatically when the user installs
 * (we set the flag on `outcome === 'accepted'`).
 *
 * Pure tenant-admin surface — no Razorpay / ArenaX / capability dependencies.
 */
import { useEffect, useRef, useState } from 'react';
import { X, Sparkles, Share, Plus, Smartphone, Monitor } from 'lucide-react';
import { usePwaInstall } from '@/lib/pwaInstall';
import { useAuth } from '@/contexts/AuthContext';
import { LOGO_URL } from '@/lib/brand';
import { trackPwa, personaForRole } from '@/lib/pwaTelemetry';

const DISMISS_KEY_LEGACY = 'outplay_prompt_dismissed';     // boolean "true" — pre-Phase 261b
const DISMISS_KEY = 'outplay_prompt_dismissed_at';         // ISO timestamp — Phase 261b
const DISMISS_TTL_DAYS = 14;
const DISMISS_TTL_MS = DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Phase 261b — Read the dismissal state from localStorage.
 * Returns { dismissed: bool, expiresAt: Date | null }
 *
 * Behavior:
 *   - No key set → not dismissed
 *   - New key has an ISO timestamp that's ≤ 14 days old → dismissed
 *   - New key timestamp > 14 days → expired, clear it, NOT dismissed
 *   - Legacy boolean key with no new key → migrate: write new key with `now` so the
 *     user gets 14 quiet days from this deploy (no surprise re-prompt today).
 */
function readDismissalState() {
  try {
    if (typeof window === 'undefined') return { dismissed: false, expiresAt: null };
    const ts = window.localStorage.getItem(DISMISS_KEY);
    const legacy = window.localStorage.getItem(DISMISS_KEY_LEGACY);

    // Migration: legacy boolean → fresh timestamp (start a new 14-day quiet window).
    if (!ts && legacy === 'true') {
      const nowIso = new Date().toISOString();
      try { window.localStorage.setItem(DISMISS_KEY, nowIso); } catch (_) {}
      try { window.localStorage.removeItem(DISMISS_KEY_LEGACY); } catch (_) {}
      return { dismissed: true, expiresAt: new Date(Date.now() + DISMISS_TTL_MS) };
    }
    if (!ts) return { dismissed: false, expiresAt: null };

    const parsed = Date.parse(ts);
    if (Number.isNaN(parsed)) {
      // Corrupt value — clear it.
      try { window.localStorage.removeItem(DISMISS_KEY); } catch (_) {}
      return { dismissed: false, expiresAt: null };
    }
    const expiresAt = new Date(parsed + DISMISS_TTL_MS);
    if (Date.now() > expiresAt.getTime()) {
      // Expired — clear so the banner re-surfaces.
      try { window.localStorage.removeItem(DISMISS_KEY); } catch (_) {}
      return { dismissed: false, expiresAt: null };
    }
    return { dismissed: true, expiresAt };
  } catch (_) {
    return { dismissed: false, expiresAt: null };
  }
}

/** Phase 261b — Public helper consumed by the Profile "Install App" card. */
export function clearPwaDismissal() {
  try {
    window.localStorage.removeItem(DISMISS_KEY);
    window.localStorage.removeItem(DISMISS_KEY_LEGACY);
    return true;
  } catch (_) {
    return false;
  }
}

/** Phase 261b — Public helper to read remaining quiet days for the Profile card. */
export function getPwaDismissalState() {
  return readDismissalState();
}

// Phase 261 — Persona-aware copy. Owner/Coach/Employee/Super-admin manage
// the academy on a desktop, so we anchor the install pitch to "taskbar +
// full-screen workspace". Students / parents check fees + progress on the
// go, so we anchor to "home-screen + push reminders".
const DESKTOP_PERSONAS = new Set(['academy_owner', 'coach', 'employee', 'super_admin']);

function copyForPersona(role) {
  if (DESKTOP_PERSONAS.has(role)) {
    return {
      pill: 'Workspace upgrade',
      headline: 'Pin Out-Play to your taskbar. Skip the browser forever.',
      sub: 'Tap the logo to install Out-Play as a standalone desktop app — your rosters, billing, and attendance open in their own full-screen window. No tabs, no URL typing, instant launch every morning.',
      Icon: Monitor,
    };
  }
  // Default: mobile-leaning persona (student, parent, player, unknown).
  return {
    pill: 'One-tap access',
    headline: 'Add Out-Play to your home screen. Catch every session.',
    sub: 'Tap the logo to install Out-Play next to your favourite apps — fee bills, progress cards, and session reminders are one tap away. No more searching browser tabs from the pitch.',
    Icon: Smartphone,
  };
}

export default function PwaInstallBanner() {
  const { canInstall, installed, isStandalone, isIos, promptInstall } = usePwaInstall();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);
  const shownEventFired = useRef(false);  // Phase 261c — only fire `pwa_banner_shown` once per mount

  useEffect(() => {
    const { dismissed: isDismissed } = readDismissalState();
    if (isDismissed) setDismissed(true);
  }, []);

  // Hide unconditionally when:
  //  - already installed
  //  - already running as standalone PWA
  //  - user dismissed (and the 14-day quiet window hasn't expired)
  //  - neither installable nor iOS-eligible
  //
  // Phase 261 — Visual QA escape hatch: `?pwa_preview=1` forces the default
  // (desktop/Chrome) variant to render regardless of beforeinstallprompt
  // state. Lets product owners screenshot-review the persona copy in any
  // browser without waiting for Chrome's install-eligibility heuristics.
  const forcedPreview = typeof window !== 'undefined'
    && window.location?.search?.includes('pwa_preview=1');

  if (installed || isStandalone || dismissed) return null;
  if (!canInstall && !isIos && !forcedPreview) return null;

  const persona = personaForRole(user?.role);
  const variant = (isIos && !canInstall) ? 'ios' : 'default';

  // Phase 261c — Funnel entry: banner became visible. Fire once per mount.
  if (!shownEventFired.current && !forcedPreview) {
    shownEventFired.current = true;
    trackPwa('pwa_banner_shown', { persona, role: user?.role, variant, source: 'banner' });
  }

  const dismissPermanent = () => {
    const dismissedAt = new Date().toISOString();
    // Phase 261b — store ISO timestamp; banner auto-resurfaces after 14 days.
    try { window.localStorage.setItem(DISMISS_KEY, dismissedAt); } catch (_) {}
    try { window.localStorage.removeItem(DISMISS_KEY_LEGACY); } catch (_) {}
    // Phase 261c — telemetry.
    trackPwa('pwa_banner_dismissed', {
      persona, role: user?.role, variant, source: 'banner', dismissed_at: dismissedAt,
    });
    setDismissed(true);
  };

  const handleLogoClick = async () => {
    if (!canInstall) return;
    setInstalling(true);
    trackPwa('pwa_install_prompted', { persona, role: user?.role, source: 'banner' });
    try {
      const accepted = await promptInstall();
      trackPwa(accepted ? 'pwa_install_accepted' : 'pwa_install_rejected', {
        persona, role: user?.role, source: 'banner',
      });
      if (accepted) {
        // Native install dialog accepted → persist dismissal so we don't
        // prompt the user again on subsequent logins.
        try { window.localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch (_) {}
        try { window.localStorage.removeItem(DISMISS_KEY_LEGACY); } catch (_) {}
        setDismissed(true);
      }
    } finally {
      setInstalling(false);
    }
  };

  // ──────────────  iOS Safari variant  ──────────────
  if (isIos && !canInstall) {
    return (
      <div
        data-testid="pwa-install-banner"
        data-variant="ios"
        className="relative mb-5 rounded-2xl border overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0c4a6e 0%, #075985 35%, #0e7490 100%)',
          borderColor: 'rgba(125, 211, 252, 0.35)',
          boxShadow: '0 10px 30px -8px rgba(6, 78, 117, 0.4)',
        }}
      >
        <button
          type="button"
          onClick={dismissPermanent}
          aria-label="Dismiss installation prompt"
          data-testid="pwa-install-banner-dismiss"
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-white/10 transition-colors text-cyan-100"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-5 py-5 sm:px-6 sm:py-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-shrink-0">
            <div className="h-14 w-14 rounded-2xl bg-white/95 flex items-center justify-center"
                 style={{ boxShadow: '0 6px 18px -4px rgba(0,0,0,0.25)' }}>
              <Smartphone className="h-7 w-7" style={{ color: '#0369a1' }} />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[10px] font-black tracking-[0.18em] uppercase text-cyan-300 mb-1">
              <Sparkles className="h-3 w-3" />
              Out-Play on your iPhone
            </div>
            <h3 className="text-white font-extrabold text-base sm:text-lg leading-tight mb-1.5">
              One-tap access — add Out-Play to your home screen
            </h3>
            <p className="text-cyan-100 text-[13px] sm:text-sm leading-relaxed">
              In Safari, tap the <strong className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 border border-white/15">
                <Share className="h-3 w-3" /> Share
              </strong> icon → scroll down → choose <strong className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 border border-white/15">
                <Plus className="h-3 w-3" /> Add to Home Screen
              </strong>. Out-Play lands beside your native apps — no browser tabs.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────  Chrome / Android / Edge / Brave variant  ──────────────
  const personaCopy = copyForPersona(user?.role);
  const PersonaIcon = personaCopy.Icon;
  return (
    <div
      data-testid="pwa-install-banner"
      data-variant="default"
      data-persona={DESKTOP_PERSONAS.has(user?.role) ? 'desktop' : 'mobile'}
      className="relative mb-5 rounded-2xl border overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #022c22 0%, #064e3b 40%, #0f766e 100%)',
        borderColor: 'rgba(110, 231, 183, 0.32)',
        boxShadow: '0 10px 30px -8px rgba(2, 44, 34, 0.5)',
      }}
    >
      <button
        type="button"
        onClick={dismissPermanent}
        aria-label="Dismiss installation prompt"
        data-testid="pwa-install-banner-dismiss"
        className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-white/10 transition-colors text-emerald-100"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="px-5 py-5 sm:px-6 sm:py-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 text-[10px] font-black tracking-[0.18em] uppercase text-emerald-300 mb-2">
            <PersonaIcon className="h-3 w-3" />
            {personaCopy.pill}
          </div>
          <h3 className="text-white font-extrabold text-base sm:text-xl leading-tight mb-1.5" data-testid="pwa-install-banner-headline">
            {personaCopy.headline}
          </h3>
          <p className="text-emerald-100 text-[13px] sm:text-sm leading-relaxed max-w-xl">
            {personaCopy.sub}
          </p>
        </div>

        {/* Interactive Logo Trigger */}
        <button
          type="button"
          onClick={handleLogoClick}
          disabled={installing}
          aria-label="Tap to install Out-Play on your device"
          data-testid="pwa-install-banner-logo"
          className="group relative flex-shrink-0 focus:outline-none focus:ring-4 focus:ring-emerald-400/40 rounded-2xl"
        >
          <div
            className={`relative h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-white p-2 sm:p-3 transition-all duration-300 cursor-pointer ${
              installing ? 'opacity-60 scale-95' : 'group-hover:scale-105 group-hover:-rotate-2 group-active:scale-95'
            }`}
            style={{ boxShadow: '0 12px 32px -6px rgba(0,0,0,0.45), 0 0 0 2px rgba(255,255,255,0.06)' }}
          >
            <img
              src={LOGO_URL}
              alt="Out-Play"
              className="h-full w-full object-contain rounded-md"
              draggable="false"
            />
            {/* Pulsing ring to draw the eye */}
            {!installing && (
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.6)',
                  animation: 'outplay-pwa-pulse 1.8s ease-out infinite',
                }}
              />
            )}
          </div>
          <div className="mt-2 text-center text-[10px] font-bold tracking-wider uppercase text-emerald-200">
            {installing ? 'Installing…' : 'Tap to install'}
          </div>
        </button>
      </div>

      {/* Keyframes — local to this component */}
      <style>{`
        @keyframes outplay-pwa-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(16, 185, 129, 0.55); }
          70%  { box-shadow: 0 0 0 16px rgba(16, 185, 129, 0); }
          100% { box-shadow: 0 0 0 0   rgba(16, 185, 129, 0); }
        }
      `}</style>
    </div>
  );
}
